import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Supabase OAuth 2.1 consent screen. Users are redirected here from the
// authorization server with an ?authorization_id query param when a client
// (e.g. ChatGPT, Claude, Cursor) asks to connect as them.
//
// Filename encodes leading-dot path segments as [.] — TanStack's route
// generator skips true dotfiles, so this must be `[.]lovable.oauth.consent`.
export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: Supabase reads its session from localStorage which isn't
  // present during SSR. Without this, signed-in users bounce to /auth.
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      // Preserve the consent URL so the auth flow returns here with the
      // same authorization_id.
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/auth", search: { next } as never });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    // supabase.auth.oauth is beta; access it via a narrow local cast.
    const client = supabase.auth as unknown as {
      oauth: {
        getAuthorizationDetails: (id: string) => Promise<{
          data: {
            client?: { name?: string; client_uri?: string };
            redirect_url?: string;
            redirect_to?: string;
            scopes?: string[];
          } | null;
          error: { message: string } | null;
        }>;
      };
    };
    const { data, error } = await client.oauth.getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="min-h-dvh bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="tracking-wide-luxury text-accent">Authorization Error</p>
        <span className="gold-divider mt-6" />
        <h1 className="mt-6 font-display text-2xl">We couldn't load this request.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {String((error as Error)?.message ?? error)}
        </p>
      </div>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const client = supabase.auth as unknown as {
      oauth: {
        approveAuthorization: (id: string) => Promise<{
          data: { redirect_url?: string; redirect_to?: string } | null;
          error: { message: string } | null;
        }>;
        denyAuthorization: (id: string) => Promise<{
          data: { redirect_url?: string; redirect_to?: string } | null;
          error: { message: string } | null;
        }>;
      };
    };
    const { data, error } = approve
      ? await client.oauth.approveAuthorization(authorization_id)
      : await client.oauth.denyAuthorization(authorization_id);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an application";

  return (
    <main className="min-h-dvh bg-background text-foreground flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center">
          <p className="tracking-wide-luxury text-accent">Grant access</p>
          <span className="gold-divider mt-6" />
          <h1 className="mt-6 font-display text-3xl">Connect {clientName}?</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {clientName} is requesting to use The Hospitality Reserve as you. It will be able
            to read your account and shifts through our official MCP tools, on your behalf.
          </p>
        </div>

        {error && (
          <p role="alert" className="mt-6 border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            {error}
          </p>
        )}

        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => decide(true)}
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-4 disabled:opacity-50"
          >
            {busy ? "Working…" : "Approve"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => decide(false)}
            className="w-full rounded-none border border-foreground/20 tracking-wide-luxury py-4 hover:bg-foreground/5 disabled:opacity-50"
          >
            Deny
          </button>
        </div>
      </div>
    </main>
  );
}
